import { useEffect, useState } from "react";
import { RunSheet } from "./RunSheet";
import { useOfficialWrite, type OfficialSheetProps } from "@/components/official/shared";
export function HeatRidersSheet(
  props: OfficialSheetProps & { remove: (path: string) => Promise<unknown> },
) {
  const [candidates, setCandidates] = useState<{ id: string; name: string }[]>([]);
  const [add, setAdd] = useState("");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [fields, setFields] = useState(["slot", "colour", "number"]);
  const [remove, setRemove] = useState<string | null>(null);
  const [loadError, setLoadError] = useState("");
  const { busy, error, write, execute, undo } = useOfficialWrite(props);
  const base = `/api/competitions/${props.competitionId}/heats/${props.heat.id}`;
  useEffect(() => {
    let active = true;
    void props.transport
      .get<{ id: string; name: string }[]>(`/api/competitions/${props.competitionId}/run-riders`)
      .then((rows) => {
        if (active) setCandidates(rows);
      })
      .catch((e) => setLoadError(e.message));
    return () => {
      active = false;
    };
  }, [props.competitionId, props.transport]);
  const disabled = busy || ["complete", "abandoned"].includes(props.heat.status);
  return (
    <RunSheet title="Heat riders" close={props.onClose}>
      <p>{props.heat.name}</p>
      {(error || loadError) && <p role="alert">{error || loadError}</p>}
      {undo}
      <ul className="official-order">
        {props.heat.riders.map((r) => (
          <li key={r.heat_rider_id}>
            <span>
              {r.bib} · {r.name}
            </span>
            <button disabled={disabled} onClick={() => setRemove(r.heat_rider_id)}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      {remove && (
        <div>
          <p>
            Remove {props.heat.riders.find((r) => r.heat_rider_id === remove)?.name} from this heat?
          </p>
          <button
            disabled={disabled}
            onClick={() => void write(() => props.remove(`${base}/riders/${remove}`))}
          >
            Confirm removal
          </button>
          <button onClick={() => setRemove(null)}>Cancel removal</button>
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void write(() => props.transport.post(`${base}/riders/${add}`));
        }}
      >
        <label>
          Add rider
          <select required value={add} onChange={(e) => setAdd(e.target.value)}>
            <option value="">Select a rider</option>
            {candidates
              .filter((c) => !props.heat.riders.some((r) => r.rider_id === c.id))
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
          </select>
        </label>
        <button className="run-primary" disabled={disabled || !add}>
          Add rider
        </button>
      </form>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void write(() => execute("swap", { a, b, fields }));
        }}
      >
        <h3>Swap riders</h3>
        {[
          ["First rider", a, setA],
          ["Second rider", b, setB],
        ].map(([label, value, setter]) => (
          <label key={String(label)}>
            {String(label)}
            <select
              required
              value={String(value)}
              onChange={(e) => (setter as (value: string) => void)(e.target.value)}
            >
              <option value="">Select a rider</option>
              {props.heat.riders.map((r) => (
                <option key={r.heat_rider_id} value={r.heat_rider_id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
        ))}
        <fieldset>
          <legend>Fields to swap</legend>
          {["slot", "colour", "number"].map((f) => (
            <label className="run-toggle" key={f}>
              <input
                type="checkbox"
                checked={fields.includes(f)}
                onChange={(e) =>
                  setFields((current) =>
                    e.target.checked ? [...current, f] : current.filter((v) => v !== f),
                  )
                }
              />
              {f[0].toUpperCase() + f.slice(1)}
            </label>
          ))}
        </fieldset>
        <button disabled={disabled || !a || !b || a === b || !fields.length}>Swap</button>
      </form>
    </RunSheet>
  );
}
